import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "./components/AppShell";
import type { AppShellTab } from "./components/AppShell";
import { LaunchTab } from "./components/LaunchTab";
import { NeedsYouTab } from "./components/NeedsYouTab";
import { ContextStripHost, logRerunRefused } from "./components/ContextStripHost";
import { InstanceDetailDrawer } from "./components/InstanceDetailDrawer";
import type { DrawerMode } from "./components/InstanceDetailDrawer";
import { workflowLabel } from "./components/CatalogCard";
import SchemaForm from "./components/SchemaForm";
import Spinner from "./components/Spinner";
import AuthProgress from "./components/StatusPanel/AuthProgress";
import StepProgress from "./components/StatusPanel/StepProgress";
import PlanReviewPanel from "./components/PlanReview/PlanReviewPanel";
import {
  orchestratorApi,
  buildAnalyzeEmailPayload,
  type AnalyzeEmailSyncResponse,
} from "./services/orchestratorApi";
import { useWorkflowStatus } from "./hooks/useWorkflowStatus";
import { useMailboxItem } from "./hooks/useMailboxItem";
import { useClientContext } from "./hooks/useClientContext";
import { ClientConfirmSection } from "@product-app/common/workflows/components/ClientConfirmSection";
import type { ActiveWorkflow, WorkflowCatalogEntry } from "./types/workflow";
import type { ClientContext, ClientSearchResult } from "./types/client";
import Skeleton from "./components/Skeleton";
import {
  threadSingleInForcePolicy,
  readPolicyOptions,
  readPolicyTermKey,
  policyTermKey,
  validateEffectiveDateAgainstPolicy,
  quarantineClientDerivedPrefill,
  POLICY_TERM_KEY_FIELD,
} from "./components/policyPrefillOptions";
import PolicyNumberCombobox from "./components/PolicyNumberCombobox";
import OldMortgageeSelect from "./components/OldMortgageeSelect";
import StateSelect from "./components/StateSelect";
import {
  composeMortgageeValues,
  decomposeMortgageePrefill,
  mortgageeAddressError,
  splitAddressSchema,
} from "./components/addressParts";
import {
  interestAutoPickPatch,
  interestResetPatch,
  readInterestOptionsForSelection,
  type InterestOption,
} from "./components/interestTargetOptions";
import { logger } from "./obs/logger";
import { emitTouchpoint } from "./obs/touchpoints";
import { readInitializationContext } from "./outlook/initializationContext";
import { openMessageInOutlook } from "./outlook/openMessage";
import {
  isSelectedEmail,
  sourceEmailFor,
  type SourceEmail,
} from "./outlook/sourceEmail";
import {
  analysisForSelectedItem,
  recordItemAnalysis,
  type SelectedItemAnalysis,
} from "./outlook/selectedItemAnalysis";
import {
  useSharedMailboxTarget,
  isSharedMailboxLaunchGated,
} from "./features/sharedMailbox/sharedMailboxTarget";
import { useLinkSharedMailboxAccess } from "./features/sharedMailbox/linkSharedMailboxAccess";
import { resolveTriageMailbox } from "./features/sharedMailbox/triageMailbox";
import { workflowRequiresClient } from "./features/launchGuards";
import { syncDrawerClient } from "./features/drawerClientSync";
import { canonicalRecommendedWorkflowId } from "@product-app/common/workflows/launch/launchGuards";
import LaunchForm from "@product-app/common/workflows/launch/LaunchForm";
import { buildLaunchBinding } from "@product-app/common/workflows/launch/launchSchemaBinding";
import type { ReanalyseResult } from "@product-app/common/workflows/launch/ReanalyseControl";
import { hasStoredPrefill, runPrefill } from "@product-app/common/workflows/launch/core/runPrefill";
import { createAddinLaunchIO } from "./launch/addinLaunchIO";
import {
  isCompactCatalogEntry,
  loadFullCatalogEntry,
} from "./services/catalogEntryCache";

// Plan glittery-stargazing-treehouse Phase 3 — the same `LaunchIO` the
// drawer's launch path will use, shared here for the prefill cycle. A single
// module-level instance: `createAddinLaunchIO` is a stateless wrapper around
// the `orchestratorApi` singleton, so there is nothing per-render to capture.
const addinLaunchIO = createAddinLaunchIO(orchestratorApi);

// ─── Drawer state type ────────────────────────────────────────────────────────

type BriefIdentity = {
  internetMessageId: string;
  mailboxAddress?: string | null;
  clientName?: string | null;
  clientSk?: number | null;
  clientCity?: string | null;
  clientState?: string | null;
  clientBranch?: string | null;
  policyNumber?: string | null;
  prefillValues?: Record<string, unknown> | null;
  /** Story 26727 — the triage record's own fields. */
  fieldConfidence?: Record<string, "high" | "medium" | "low"> | null;
  triageOutcome?: string | null;
  instanceId?: string | null;
  /** Graph id of the brief's email — what Open Email hands to Outlook, and
   *  what a launch from the Today list replies to (see `BriefEmail`). */
  graphMessageId?: string | null;
};

/**
 * The email a queued brief is about, when it is NOT the item selected in
 * Outlook — the Servicer opened it from the Today list. Everything the drawer
 * would otherwise read off `emailContext` (the prefill source, the launch's
 * Graph id, the re-analyse identity) comes from here instead: the selected
 * item is a different email, or none at all.
 */
type BriefEmail = {
  internetMessageId: string;
  mailboxAddress: string | null;
  graphMessageId: string | null;
};

function readClientSk(...candidates: unknown[]): number | null {
  for (const value of candidates) {
    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
      return value;
    }
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  }
  return null;
}

type DrawerState = {
  open: boolean;
  mode: DrawerMode;
  workflowRef?: string;
  /** Full catalog entry — required in pre_launch mode so the drawer can
   *  render input_schema + description. Set by handleOpenCatalog when a
   *  catalog card is clicked. */
  catalogEntry?: WorkflowCatalogEntry | null;
  instanceId?: string;
  workflowId?: string;
  idempotencyKey?: string;
  prefill?: Record<string, unknown>;
  fieldConfidence?: Record<string, "high" | "medium" | "low">;
  prefillLoading?: boolean;
  clientSk?: number | null;
  clientName?: string | null;
  /** True when the pre-launch drawer was opened by a context-strip action
   *  (Relaunch / Launch another / Open the brief). Only such a launch logs a
   *  re-run refusal — a refusal on the ordinary catalog path is not a re-run. */
  fromContextStrip?: boolean;
  /**
   * running-drawer AC-9: the email identity of the RUN a reuse came from.
   * Kept OUT of `prefill` on purpose — `internetMessageId` is a separate drawer
   * prop sourced from `emailContext` at the render site, and a reuse triggered
   * from a run drawer has no guarantee the selected mail item is the one the
   * run was launched from. Cleared on drawer close so a later catalog-open
   * launch falls back to `emailContext` and cannot inherit a stale run's
   * identity.
   */
  reuseIdentity?: string | null;
  /** Set when the open brief's email is not the selected item — see
   *  `BriefEmail`. Cleared on drawer close, like `reuseIdentity`. */
  briefEmail?: BriefEmail | null;
  /**
   * Story 26752 AC-5: the Re-analyse control is withheld from LaunchForm
   * (never rendered, not just disabled) once a response has told us the
   * signed-in caller lacks operator permission. `undefined` — no analyze
   * response has landed for THIS brief yet — is treated as permissive so the
   * control keeps its current always-shown behaviour until the server says
   * otherwise; only an explicit `false` hides it. The actual boundary is the
   * server's own [Authorize] on POST reanalyze_email — this is a UI hint,
   * never re-derived or cached across briefs.
   */
  canReanalyze?: boolean;
  /**
   * The email this pre-launch form was prepared from, captured at open. Drives
   * the form's Open Email link: a brief opened from Ready to start (typically
   * while an ora notification is the selected item) is not the email on
   * screen, and this is the only way back to it.
   */
  sourceEmail?: SourceEmail | null;
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Story 28614: what the pane says, under the strip, when a request to open a
 *  launch form opened nothing. `{workflow}` is the workflow's display name. */
export const LAUNCH_UNAVAILABLE_NOTICE =
  "{workflow} isn't available to start. Choose another under Start a workflow on that email.";
export const LAUNCH_CATALOG_FAILED_NOTICE =
  "Couldn't load the workflow list. Try again in a moment.";

// analyze_email meta-keys that are structural, not schema fields. Stripped from
// `lastExtraction` before it's passed to triggerPrefill as already_extracted
// (the backend filters to schema fields anyway, but this keeps the payload clean).
const EXTRACTION_META_KEYS = new Set([
  "confidence",
  "client_sk",
  "client_name",
  "client_email",
]);

/** Strip analyze_email meta-keys; returns the extracted schema fields or undefined when empty. */
function stripExtractionMeta(
  extraction: Record<string, unknown> | null,
): Record<string, unknown> | undefined {
  if (!extraction) return undefined;
  const fields: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(extraction)) {
    if (!EXTRACTION_META_KEYS.has(k)) fields[k] = v;
  }
  return Object.keys(fields).length > 0 ? fields : undefined;
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  // ── Demo mode ──────────────────────────────────────────────────────────────
  const [demoMode, setDemoMode] = useState<boolean>(
    () => localStorage.getItem("ora_demo_mode_addin") === "true",
  );

  // ── Tab state ──────────────────────────────────────────────────────────────
  // QUEUE FIRST (story pinned-pane, AC-2): the pane opens on what is waiting
  // on this AM, not on a launch form. `launch` and `active` are untouched.
  const [currentTab, setCurrentTab] = useState<AppShellTab>("needs_you");
  /** Why the last request to open a launch form opened nothing — shown under
   *  the context strip, where Start a workflow is (story 28614). */
  const [launchNotice, setLaunchNotice] = useState<string | null>(null);
  const paneTab: AppShellTab =
    currentTab === "active" ? "needs_you" : currentTab;

  // ── Waiting-queue badge — the SERVER's count, forwarded by NeedsYouTab ─────
  const [needsYouCount, setNeedsYouCount] = useState(0);

  // ── Auto-suggested workflow ref (set by analyze_email result) ─────────────
  const [autoSuggestedRef, setAutoSuggestedRef] = useState<string | null>(null);

  // Combined-prefill auto-open is retired. A prepared brief is opened by the
  // context-strip "Open the brief" action, not by analyzing the email.

  // ── The last analyze_email result, stored WITH the item it was run for ────
  // Stashed before resetWorkflow() clears suggestion so it can enrich prefill
  // when a catalog tile is clicked (e.g. policy_number for carrier workflows),
  // and so the PDF/attachment text reaches triggerPrefill (it is NOT an
  // extraction field — it must not leak into already_extracted). Read through
  // `lastExtraction` / `lastAttachmentText` below, which are the SELECTED
  // item's only (bug 28610 — see outlook/selectedItemAnalysis.ts).
  const [itemAnalysis, setItemAnalysis] = useState<SelectedItemAnalysis | null>(null);

  // ── KPI metrics (lifted from LaunchTab for the analyzing overlay) ──────────
  const [kpiMetrics, setKpiMetrics] = useState<{ total: number; hours_saved: number } | null>(null);

  // ── Active instance count — driven by LaunchTab's useActiveInstances ─────
  const [activeCount, setActiveCount] = useState(0);

  // ── Catalog — read ONCE on mount, COMPACT (plugin catalog round, 2026-10-06).
  //    Names, descriptions and card fields for the context strip, queue and
  //    drawer labels, and the Launch view — which renders THIS copy (it used to
  //    re-read the full ~81 KB catalog on every open). A form or a run reads its
  //    workflow's FULL entry itself (InstanceDetailDrawer → useFullCatalogEntry);
  //    nothing here ever needs a schema or a step list. The ref keeps lookups
  //    render-free and still allows the retry-after-a-miss below.
  const catalogMapRef = useRef<Map<string, WorkflowCatalogEntry>>(new Map());
  const [catalogEntries, setCatalogEntries] = useState<WorkflowCatalogEntry[]>([]);
  const [catalogLoad, setCatalogLoad] = useState<{ loading: boolean; error: unknown }>({
    loading: true,
    error: null,
  });
  // ONE pending read: a missing-workflow refresh or a Retry that arrives while a
  // read is in flight joins it, so a fast second failure cannot land on an empty
  // list and an older response cannot overwrite a newer one.
  const catalogInflightRef = useRef<Promise<WorkflowCatalogEntry[]> | null>(null);
  const refreshCatalogMap = useCallback((): Promise<WorkflowCatalogEntry[]> => {
    if (catalogInflightRef.current) return catalogInflightRef.current;
    const read = readCatalogOnce();
    catalogInflightRef.current = read;
    const clear = () => {
      if (catalogInflightRef.current === read) catalogInflightRef.current = null;
    };
    read.then(clear, clear);
    return read;
    async function readCatalogOnce(): Promise<WorkflowCatalogEntry[]> {
    // A retry after a failure shows the skeleton again; a re-read behind a list
    // already on screen does not.
    setCatalogLoad((prev) => (prev.error ? { loading: true, error: null } : prev));
    try {
      const entries = await orchestratorApi.getCatalog({ compact: true });
      const catalog = Array.isArray(entries) ? entries : [];
      catalogMapRef.current = new Map(catalog.map((entry) => [entry.id, entry]));
      setCatalogEntries(catalog);
      setCatalogLoad({ loading: false, error: null });
      return catalog;
    } catch (error) {
      // A failed RE-read keeps the list already shown; only a pane with no list
      // at all says the catalog could not be loaded.
      setCatalogLoad(
        catalogMapRef.current.size > 0
          ? { loading: false, error: null }
          : { loading: false, error },
      );
      throw error;
    }
    }
  }, []);
  useEffect(() => {
    void refreshCatalogMap().catch(() => {});
  }, [refreshCatalogMap]);
  const retryCatalog = useCallback(() => {
    void refreshCatalogMap().catch(() => {});
  }, [refreshCatalogMap]);
  /** What the Launch view renders. */
  const launchCatalogSource = useMemo(
    () => ({
      entries: catalogEntries,
      loading: catalogLoad.loading,
      error: catalogLoad.error,
      onRetry: retryCatalog,
    }),
    [catalogEntries, catalogLoad, retryCatalog],
  );

  // ── Client context (lifted so LaunchTab + Drawer share state) ─────────────
  const emailContext = useMailboxItem();
  const clientCtx = useClientContext(emailContext);
  // Derived per render, so a switch to another email hides the previous one's
  // analysis in the same render the selection changes — no effect to order.
  const selectedAnalysis = analysisForSelectedItem(itemAnalysis, emailContext?.emailId);
  const lastExtraction = selectedAnalysis?.extraction ?? null;
  const lastAttachmentText = selectedAnalysis?.attachmentText ?? null;

  // ── Shared-mailbox launch target (plan Phase 3 task 1) ────────────────────
  // Mounted ONCE here — populates the module-level store that
  // orchestratorApi.triggerWorkflow reads at the injection point, and
  // re-detects on Office ItemChanged. Passed down to LaunchTab for rendering
  // + launch gating; InstanceDetailDrawer needs no wiring (the store is read
  // as a plain function at the triggerWorkflow call site, not via props).
  const sharedMailboxTarget = useSharedMailboxTarget();
  // Opening an email from a shared mailbox links the person to it (the substrate
  // checks the message is in that mailbox and recent; only an enrolled mailbox).
  useLinkSharedMailboxAccess(
    sharedMailboxTarget.status,
    sharedMailboxTarget.targetMailbox,
    emailContext?.restId ?? null,
  );

  // ── Workflow status hook ───────────────────────────────────────────────────
  const {
    phase,
    failureContext,
    workflowId,
    plan,
    status,
    artifacts,
    suggestion,
    analyzeEmail,
    approvePlan,
    rejectPlan,
    resetWorkflow,
    isConnected,
    isSignalRUnavailable,
  } = useWorkflowStatus();

  // Analyze only when the AM clicks "Run analysis". Auto-analyzing every
  // selected email was minting empty cache rows that the strip then treated
  // as a ready brief.
  const handleProcess = useCallback(async () => {
    if (!emailContext) return;
    await analyzeEmail(emailContext);
  }, [emailContext, analyzeEmail]);

  // ── Auto-advance client_context phase ─────────────────────────────────────
  // When analyze_email returns a client suggestion, hydrate the client tile
  // directly from the pre-fetched metadata (no extra API round-trips).
  // Falls through to idle whether or not a match was found so the LaunchTab
  // is always shown for the user to confirm or override.
  useEffect(() => {
    if (phase !== "client_context") return;
    if (suggestion?.client_context?.client) {
      // Old workflow path: substrate returned full ClientContext with clientSk.
      clientCtx.hydrateFromContext(suggestion.client_context);
    } else if (suggestion?.suggestions && suggestion.suggestions.length > 0) {
      // Low-confidence multi-match: the substrate found several candidates for
      // the email's client name but couldn't pick one. Surface them as a "did
      // you mean" picker instead of guessing — the operator selects one (or
      // dismisses to the manual search box). Checked before client_name so the
      // ambiguous case never silently auto-searches.
      clientCtx.showSuggestions(suggestion.suggestions);
    } else if (suggestion?.client_name) {
      // New sync analyze_email path: substrate resolves the name from the email
      // but doesn't perform the SQL lookup (no clientSk in the response).
      // beginLookupByName immediately transitions to "searching" (hides the
      // search box) then auto-selects when the result is unambiguous.
      void clientCtx.beginLookupByName(suggestion.client_name);
    }
    if (suggestion?.recommended_workflow_id) {
      setAutoSuggestedRef(
        canonicalRecommendedWorkflowId(suggestion.recommended_workflow_id),
      );
    }
    if (suggestion) {
      // Stash extraction fields (minus structural keys) so handleOpenCatalog
      // can include e.g. policy_number in the workflow prefill. suggestions is a
      // structural key (the candidate list) — keep it out of the prefill payload.
      const rest = { ...suggestion } as Record<string, unknown>;
      delete rest.recommended_workflow_id;
      delete rest.client_context;
      delete rest.suggestions;
      delete rest.attachment_text;
      delete rest.source_email_id;
      // Filed under the email the analysis was RUN for, not whichever one is
      // selected when it lands — the AM may have moved on mid-analysis. The
      // PDF/attachment text rides separately (not an extraction field) into
      // triggerPrefill → workflow_prefill.attachment_text.
      setItemAnalysis((prev) =>
        recordItemAnalysis(prev, suggestion.source_email_id, {
          extraction: Object.keys(rest).length > 0 ? rest : undefined,
          attachmentText: suggestion.attachment_text ?? null,
        }),
      );
    }
    resetWorkflow();
  }, [phase, suggestion]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-close: REMOVED ───────────────────────────────────────────────────
  // A completed run and an auth failure each used to start a 10s timer that
  // called `Office.addin.hide()` and dismissed the whole task pane. A PINNED
  // PANE MUST NOT BE CLOSED — the Servicer pinned it so it would stay — and
  // Office.js gives no way to ask whether they pinned it, so there is no
  // version of this that is safe to keep. Both panels now simply stay up until
  // the Servicer moves on, which is also the only way to read a result that
  // took longer than the budget to arrive.
  //
  // `resetWorkflow` is unaffected: it still runs from the phase effect above.
  // Nothing in this add-in closes the pane on a timer any more, and the
  // `useAutoClose` hook that existed only to do it is gone.

  // ── Drawer state ──────────────────────────────────────────────────────────
  const [drawerState, setDrawerState] = useState<DrawerState>({
    open: false,
    mode: "pre_launch",
  });

  // The launch notice speaks for ONE request on ONE email: a form opening, or
  // another email selected, retires it.
  useEffect(() => {
    if (drawerState.open) setLaunchNotice(null);
  }, [drawerState.open]);
  useEffect(() => {
    setLaunchNotice(null);
  }, [emailContext?.internetMessageId]);

  // Incremented on every catalog open so the drawer remounts (clearing stale form values).
  const [drawerOpenCount, setDrawerOpenCount] = useState(0);

  // Bumped on HITL submit to nudge NeedsYouTab's queue into an early refetch
  // instead of waiting on its 30-60s poll floor. Burst rather than a single
  // bump for the same reason as ownPush's ladder below: SignalR does not
  // reliably fire a push event for this transition, so the first re-read can
  // still race the server's write.
  const [needsYouRefreshSignal, setNeedsYouRefreshSignal] = useState(0);
  const handleHitlResponded = useCallback(() => {
    const bump = () => setNeedsYouRefreshSignal((n) => n + 1);
    bump();
    [2_000, 5_000, 10_000, 20_000].forEach((delay) => {
      setTimeout(bump, delay);
    });
  }, []);

  // Split-address form (2026-08-03): every mortgagee item entering the form
  // carries the four address part keys — server-emitted parts pass through, a
  // legacy single-string mailing_address is split client-side, and an
  // unsplittable string lands whole in Street for the AM to fix. This is the
  // ONE seam between prefill and the drawer's form values, covering the
  // catalog-open, background-merge, and CTA paths alike.
  const decomposedPrefill = useMemo(
    () =>
      drawerState.prefill
        ? decomposeMortgageePrefill(drawerState.prefill)
        : drawerState.prefill,
    [drawerState.prefill],
  );

  // Cancels any in-flight background prefill when the drawer closes or a new
  // catalog entry is opened before the previous call completes.
  const prefillCancelRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  // Background prefill fetch — shared by the initial catalog open and the
  // policy-dropdown re-resolve so both paths merge `prefill_values` (incl.
  // `policy_options` and `policy_number_resolution`) identically.
  //
  // AWAITABLE half. RE-ANALYSE needs to know when the merge has landed (its
  // control reports working/failed and `LaunchForm` drops the action list on
  // SUCCESS only), so the body is a promise the caller can await and a failure
  // REJECTS instead of being swallowed. `runBackgroundPrefill` below keeps the
  // fire-and-forget contract its three existing callers rely on. ONE merge
  // path either way — a second one is how the two drift.
  const runPrefillFetch = useCallback(
    async (
      entry: WorkflowCatalogEntry,
      clientSk: number | null,
      sourceText: string,
      alreadyExtracted: Record<string, unknown> | undefined,
      attachmentText: string | null,
      cancel: { cancelled: boolean },
      // TRUE only for RE-ANALYSE: `triggerPrefill` keys idempotency on a hash of
      // its inputs, so an unchanged re-press returns the SAME completed instance
      // and the SAME stale values (PAD-4). `reanalysePrefill` is that call with a
      // fresh key per press and nothing else changed.
      fresh = false,
      messageIdentity?: string | null,
      mailbox?: string | null,
    ): Promise<void> => {
      try {
        // Plan glittery-stargazing-treehouse Phase 3: the trigger → poll →
        // cancel-check → store (RAW confidence) → quarantine cycle moved to
        // the shared `runPrefill` core module verbatim — ora's triage route
        // calls the SAME function. Returns `null` when the cancel-ref fired
        // right after the poll resolved (App.tsx's own cancel check used to
        // sit here); the core already performs that check before the store
        // call, so a cancelled prefill neither writes a stale value onto the
        // analysis row nor onto drawer state.
        const result = await runPrefill({
          io: addinLaunchIO,
          workflowId: entry.id,
          clientSk,
          sourceText,
          alreadyExtracted,
          attachmentText,
          fresh,
          messageId: messageIdentity,
          mailbox,
          cancel,
        });
        if (result === null) return;
        // `result.values` / `result.confidence` are already quarantined
        // (clientSk === null) by the core — see runPrefill.ts's own
        // docstring for why that happens AFTER the (always-RAW) store.
        setDrawerState((prev) => {
          if (!prev.open) return prev;
          return {
            ...prev,
            prefillLoading: false,
            prefill: { ...(prev.prefill ?? {}), ...result.values },
            fieldConfidence: {
              ...(prev.fieldConfidence ?? {}),
              ...(result.confidence as
                | Record<string, "high" | "medium" | "low">
                | undefined),
            },
          };
        });
      } catch (err) {
        if (cancel.cancelled) return;
        setDrawerState((prev) => ({ ...prev, prefillLoading: false }));
        throw err;
      }
    },
    [],
  );

  const runBackgroundPrefill = useCallback(
    (
      entry: WorkflowCatalogEntry,
      clientSk: number | null,
      sourceText: string,
      alreadyExtracted: Record<string, unknown> | undefined,
      attachmentText: string | null,
      cancel: { cancelled: boolean },
      messageIdentity?: string | null,
      mailbox?: string | null,
    ) => {
      // Background: a failed prefill leaves the form empty and the AM fills it
      // in — it has never surfaced an error, and this wrapper keeps that.
      void runPrefillFetch(
        entry,
        clientSk,
        sourceText,
        alreadyExtracted,
        attachmentText,
        cancel,
        false,
        messageIdentity,
        mailbox,
      ).catch(() => {});
    },
    [runPrefillFetch],
  );

  const handleOpenCatalog = useCallback(
    (entry: WorkflowCatalogEntry, opts?: {
      fromContextStrip?: boolean;
      briefIdentity?: BriefIdentity;
      /** Open the form in a loading state without starting prefill — the caller
       *  will analyze first, then run prefill itself. */
      deferPrefill?: boolean;
    }) => {
      // Cancel any previous in-flight prefill and create a new guard.
      prefillCancelRef.current.cancelled = true;
      const cancel = { cancelled: false };
      prefillCancelRef.current = cancel;

      const clientPrefill = clientCtx.clientContext
        ? {
            client_sk: clientCtx.clientContext.client.client_sk,
            client_name: clientCtx.clientContext.client.client_name,
          }
        : {};
      // Build per-field confidence map: every extracted key (except the
      // meta "confidence" key itself) shares the same overall confidence level.
      const confLevel = lastExtraction?.confidence as "high" | "medium" | "low" | undefined;
      const fieldConfidence: Record<string, "high" | "medium" | "low"> | undefined = confLevel
        ? Object.fromEntries(
            Object.keys(lastExtraction ?? {})
              .filter((k) => k !== "confidence")
              .map((k) => [k, confLevel]),
          )
        : undefined;

      const briefIdentity = opts?.briefIdentity;
      const isCurrentEmail = !briefIdentity
        || Boolean(
          emailContext?.internetMessageId
          && briefIdentity.internetMessageId === emailContext.internetMessageId,
        );
      // The selected item's body is a prefill source only for ITS OWN email.
      const hasLocalSource = Boolean(emailContext?.body) && isCurrentEmail;
      // A brief opened from the Today list for an email that is not the
      // selection. Working from the list must not need the email open, so the
      // brief's own email stands in for `emailContext` — see `BriefEmail`.
      const briefEmail: BriefEmail | null =
        briefIdentity && !isCurrentEmail
          ? {
              internetMessageId: briefIdentity.internetMessageId,
              mailboxAddress: briefIdentity.mailboxAddress ?? null,
              graphMessageId: briefIdentity.graphMessageId ?? null,
            }
          : null;
      // No body in hand, but the brief's identity is enough: workflow_prefill
      // recovers the body over Graph and the attachment text from the stored
      // row (the same path ora's triage page takes).
      const recoversSource = Boolean(briefIdentity) && !hasLocalSource;
      const hasPrefillSource = hasLocalSource || recoversSource;

      // When hasPrefillSource is true and no client context is resolved,
      // seed client_name from the extraction so it flows into values → next →
      // inputs. The substrate reads inputs.client_name via emit_push to derive
      // metadata; without this hint the client name is lost for workflows where
      // the lookup returned multiple matches or wasn't triggered.
      const extractionClientHint: Record<string, unknown> =
        !clientCtx.clientContext && lastExtraction?.client_name
          ? { client_name: lastExtraction.client_name as string }
          : {};

      // Paint the cached analyze_email extraction immediately; for a fresh,
      // un-briefed open a background prefill then enriches policy_options /
      // interest options. A brief with a STORED prefill (a complete result:
      // intake computes it when it prepares the brief, story 28795) runs no
      // background prefill at all -- see `hasStoredPrefill`. Quarantine
      // client-derived keys when no client is selected (same ordering fix as
      // the background merge).
      const extractionFields = stripExtractionMeta(lastExtraction) ?? {};
      const seededExtraction = clientCtx.clientContext
        ? extractionFields
        : quarantineClientDerivedPrefill(extractionFields);
      const cachedPrefill = briefIdentity?.prefillValues ?? {};
      const hasCachedPrefill = hasStoredPrefill(briefIdentity?.prefillValues);
      // For a brief email the selected item's resolved client belongs to a
      // DIFFERENT email, so it never outranks the brief's own client.
      const selectedClient = briefEmail ? null : clientCtx.clientContext;
      const restoredClientSk = readClientSk(
        selectedClient?.client.client_sk,
        briefIdentity?.clientSk,
        cachedPrefill.client_sk,
      );
      const restoredClientName =
        selectedClient?.client.client_name
        ?? briefIdentity?.clientName
        ?? (typeof cachedPrefill.client_name === "string"
          ? cachedPrefill.client_name
          : null);
      const briefPrefill: Record<string, unknown> = briefIdentity
        ? {
            ...(briefIdentity.clientName
              ? { client_name: briefIdentity.clientName }
              : {}),
            ...(briefIdentity.policyNumber
              ? { policy_number: briefIdentity.policyNumber }
              : {}),
            ...(restoredClientSk ? { client_sk: restoredClientSk } : {}),
            ...cachedPrefill,
          }
        : {};
      const prefill: Record<string, unknown> = briefIdentity
        ? briefPrefill
        : {
            ...seededExtraction,
            ...extractionClientHint,
            ...clientPrefill,
          };

      setDrawerOpenCount((c) => c + 1);
      setDrawerState({
        open: true,
        mode: "pre_launch",
        workflowRef: entry.ref,
        catalogEntry: entry,
        prefill,
        // Story 26727: a brief carries the SERVER's per-field confidence —
        // what the sweep/prefill actually produced, not a client-side
        // recomputation. Only on the cached-brief path (briefIdentity
        // present); the extraction fan-out below stays the answer for a
        // fresh, un-briefed open.
        fieldConfidence: briefIdentity?.fieldConfidence ?? fieldConfidence,
        prefillLoading:
          Boolean(opts?.deferPrefill)
          || (hasPrefillSource && !hasCachedPrefill),
        clientSk: restoredClientSk,
        clientName: restoredClientName,
        fromContextStrip: opts?.fromContextStrip ?? false,
        reuseIdentity: briefIdentity?.internetMessageId ?? null,
        briefEmail,
        sourceEmail: sourceEmailFor(briefIdentity, emailContext),
      });

      const existing = clientCtx.clientContext;
      const locationMissing = Boolean(
        existing
        && existing.client.client_sk === restoredClientSk
        && !existing.client.city
        && !existing.client.branch_name,
      );
      if (
        restoredClientSk
        && (restoredClientSk !== existing?.client.client_sk || locationMissing)
      ) {
        // Only the SAME client's resolved details may fill gaps: carrying a
        // different client's id and policies onto this one would show the
        // Servicer another client's in-force list.
        const sameClient =
          existing?.client.client_sk === restoredClientSk ? existing : null;
        const snapshot: ClientContext = {
          client: {
            client_sk: restoredClientSk,
            client_id: sameClient?.client.client_id ?? "",
            client_name: restoredClientName ?? sameClient?.client.client_name ?? "",
            city: briefIdentity?.clientCity ?? sameClient?.client.city,
            state: briefIdentity?.clientState ?? sameClient?.client.state,
            branch_name: briefIdentity?.clientBranch ?? sameClient?.client.branch_name,
          },
          policies_in_force: sameClient?.policies_in_force ?? [],
          opportunities: sameClient?.opportunities ?? [],
          client_pick_confidence: sameClient?.client_pick_confidence,
        };
        if (snapshot.client.city || snapshot.client.branch_name) {
          clientCtx.hydrateFromContext(snapshot);
        } else {
          void clientCtx.selectClient(restoredClientSk);
        }
      } else if (
        !restoredClientSk
        && restoredClientName
        && !clientCtx.clientContext
      ) {
        void clientCtx.beginLookupByName(restoredClientName);
      }

      if (
        recoversSource
        && briefIdentity
        && !opts?.deferPrefill
        && !hasCachedPrefill
      ) {
        // Blank text + the brief's identity. No already_extracted: both
        // `lastExtraction` and the client context's in-force policies belong
        // to the selected item, not this brief — the same empty seed ora's
        // triage page sends for a brief with no stored prefill.
        runBackgroundPrefill(
          entry,
          restoredClientSk,
          "",
          {},
          null,
          cancel,
          briefIdentity.internetMessageId,
          briefIdentity.mailboxAddress ?? null,
        );
      } else if (hasLocalSource && !opts?.deferPrefill && !hasCachedPrefill) {
        const clientSk =
          restoredClientSk ?? clientCtx.clientContext?.client.client_sk ?? null;
        // prefill-query-collapse (AC#2): on the FLAT contract, when the
        // CURRENTLY-selected client has a single qualifying in-force policy,
        // thread its number+carrier+type into already_extracted so prefill needs
        // no EDW query. Re-derived from the current clientCtx at call time (never
        // cached) — a stale mid-flow client switch can't thread the prior
        // client's data; threadChosenPolicy's membership check guarantees
        // correlation with this client.
        //
        // NOT on a reshaped (v2) contract (story 28631): the substrate's
        // zero-query bypass skips the policy + interest lookups, so the term
        // identity, the Epic codes (`ams_policy_type_code`, `clientlookup_code`)
        // and the interest options would never reach the form. v2 sends the
        // extraction only, like ora and the multi-policy path.
        const inForce = clientCtx.clientContext?.policies_in_force ?? [];
        const emailBody = emailContext!.body;
        const messageId = emailContext?.internetMessageId;
        const extraction = stripExtractionMeta(lastExtraction) ?? {};
        const attachmentText = lastAttachmentText;
        void (async () => {
          // The pane's catalog list is COMPACT (no input_schema): read the full
          // entry, which the drawer needs anyway, so this is a cache hit or the
          // read it would have made. If it cannot be read the form cannot render
          // either, and the safe answer is the full resolution (no threading).
          let reshaped = true;
          try {
            const full = isCompactCatalogEntry(entry)
              ? await loadFullCatalogEntry(entry.id)
              : entry;
            reshaped = buildLaunchBinding(full.input_schema ?? null).isReshaped;
          } catch {
            // keep reshaped = true
          }
          if (cancel.cancelled) return;
          // lastAttachmentText (dev): the substrate's PDF/attachment text is
          // threaded into triggerPrefill so prefill can extract PDF-only
          // identifiers.
          runBackgroundPrefill(
            entry,
            clientSk,
            emailBody,
            {
              ...extraction,
              ...threadSingleInForcePolicy(inForce, clientSk, { reshaped }),
            },
            attachmentText,
            cancel,
            messageId,
          );
        })();
      }
    },
    [clientCtx.clientContext, lastExtraction, lastAttachmentText, emailContext, runBackgroundPrefill],
  );

  // ── Shared-mailbox launch gate for the Active tab (mirrors LaunchTab's
  // gatedOpenCatalog). The only gate is "pending": the flag/detection round-trip
  // is still in flight, so a click must not slip through before we know shared vs
  // personal. Once resolved, launches proceed and a shared target threads
  // automatically (no confirmation).
  const activeTabLaunchGated = isSharedMailboxLaunchGated(
    sharedMailboxTarget.status,
  );
  const gatedHandleOpenCatalog = useCallback(
    (entry: WorkflowCatalogEntry) => {
      if (activeTabLaunchGated) return;
      handleOpenCatalog(entry);
    },
    [activeTabLaunchGated, handleOpenCatalog],
  );

  // ── Pre-launch drawer for a NAMED workflow (context strip: Open the brief /
  //    Relaunch / Launch another). It OPENS THE FORM and returns — it never
  //    creates a run, never mutates the prior run and never references it as a
  //    parent (operator decision 2026-08-25: a one-click re-run would bypass
  //    every pre-dispatch guard). The declared per-email identity travels the
  //    way it already does, on the drawer's `internetMessageId` prop — no new
  //    launch-payload field (design Constraint 23).
  //
  //    An unresolvable workflow opens NOTHING and says so under the strip,
  //    rather than guessing a workflow on the AM's behalf (story 28614).
  const handleOpenPreLaunchForWorkflow = useCallback(
    async (
      workflowId: string | null,
      briefIdentity?: BriefIdentity,
      opts?: { deferPrefill?: boolean },
    ) => {
      if (activeTabLaunchGated) return;
      setLaunchNotice(null);

      const canonicalId = canonicalRecommendedWorkflowId(workflowId);
      let entry = canonicalId
        ? catalogMapRef.current.get(canonicalId)
        : undefined;
      // FALLBACK: we cannot open the workflow asked for — the catalog does not
      // hold it (a brief or a Relaunch naming a workflow no longer offered) or
      // the refresh-on-miss read failed. This used to open a full-frame
      // "Choose a workflow" page; that page is gone (story 28614). Say why in
      // one line under the strip, where Start a workflow already offers the
      // list — never a silent no-op, never a guess.
      if (!entry && canonicalId) {
        try {
          await refreshCatalogMap();
          entry = catalogMapRef.current.get(canonicalId);
        } catch {
          setLaunchNotice(LAUNCH_CATALOG_FAILED_NOTICE);
          return;
        }
      }
      if (!entry) {
        setLaunchNotice(
          LAUNCH_UNAVAILABLE_NOTICE.replace(
            "{workflow}",
            workflowId ? workflowLabel(canonicalId ?? workflowId) : "That workflow",
          ),
        );
        return;
      }
      // 26747: opening a prepared brief. Emitted HERE rather than at each
      // caller, because there are three ways in (the Today queue's card, the
      // context strip's primary action, the ora-notification landing) and a
      // per-caller emit would silently miss whichever one is added next. The
      // matching `leave` fires from `handleDrawerClose`.
      emitTouchpoint("brief_review", "arrive", { workflow_id: entry.id });
      handleOpenCatalog(entry, {
        fromContextStrip: true,
        briefIdentity,
        deferPrefill: opts?.deferPrefill,
      });
    },
    [activeTabLaunchGated, handleOpenCatalog, refreshCatalogMap],
  );

  /** Apply an analyze/reanalyze result onto the client tile. Returns the sk when known.
   *  `forItemKey` is the `emailId` of the selected item the analysis was run
   *  for; the result is stashed under it and seeds that item's next launch.
   *  `null` (a brief opened from the list for another email) keeps it out of
   *  the stash entirely. */
  const applyAnalysisClient = useCallback(
    (analysis: AnalyzeEmailSyncResponse, forItemKey: string | null): number | null => {
      const fresh = analysis.extraction as unknown as Record<string, unknown>;
      if (forItemKey) {
        // `attachment_text` undefined = not reported: keep this item's earlier
        // text, never another item's.
        setItemAnalysis((prev) =>
          recordItemAnalysis(prev, forItemKey, {
            extraction: fresh,
            attachmentText: analysis.attachment_text,
          }),
        );
      }
      // Story 26752 AC-5. `can_reanalyze` is absent on any response shape that
      // predates this field (a stale cached response) — undefined leaves the
      // drawer's own permissive default alone rather than overwriting it with
      // a fail-closed false the server never actually sent.
      if (analysis.can_reanalyze !== undefined) {
        const canReanalyze = analysis.can_reanalyze;
        setDrawerState((prev) =>
          prev.open ? { ...prev, canReanalyze } : prev,
        );
      }
      const matched = analysis.client_context?.client;
      if (matched) {
        clientCtx.hydrateFromContext(analysis.client_context!);
        return matched.client_sk;
      }
      const suggestions = (analysis.suggested_clients ?? [])
        .filter((s) => s.client_sk != null)
        .map((s) => ({
          clientSk: s.client_sk as number,
          clientName: s.client_name ?? "",
          branchName: s.branch_name ?? undefined,
          city: s.city ?? undefined,
          state: s.state ?? undefined,
        }));
      if (suggestions.length > 0) {
        clientCtx.showSuggestions(suggestions);
        return null;
      }
      if (analysis.extraction?.client_name) {
        void clientCtx.beginLookupByName(analysis.extraction.client_name);
      }
      return null;
    },
    [clientCtx],
  );

  /**
   * AM-picked workflow: open the form, then identify the client and prefill.
   * Does NOT ask the model to recommend a workflow — mailbox intake still does.
   */
  const handleStartSelectedWorkflow = useCallback(
    async (workflowId: string) => {
      await handleOpenPreLaunchForWorkflow(workflowId, undefined, {
        deferPrefill: true,
      });
      if (!emailContext) return;
      const canonicalId = canonicalRecommendedWorkflowId(workflowId);
      const entry = canonicalId
        ? catalogMapRef.current.get(canonicalId)
        : undefined;
      if (!entry) return;
      const cancel = prefillCancelRef.current;
      try {
        const analysis = await orchestratorApi.analyzeEmailSync({
          ...buildAnalyzeEmailPayload(emailContext),
          skip_workflow_recommendation: true,
          selected_workflow_id: canonicalId ?? workflowId,
        });
        const clientSk = applyAnalysisClient(analysis, emailContext.emailId || null);
        setDrawerState((prev) =>
          prev.open
            ? {
                ...prev,
                clientSk,
                clientName:
                  analysis.client_context?.client?.client_name ?? prev.clientName,
                prefillLoading: true,
              }
            : prev,
        );
        await runPrefillFetch(
          entry,
          clientSk,
          emailContext.body,
          stripExtractionMeta(
            analysis.extraction as unknown as Record<string, unknown>,
          ),
          analysis.attachment_text ?? lastAttachmentText,
          cancel,
          false,
          emailContext.internetMessageId,
        );
      } catch (err) {
        logger.warn("App: start-selected-workflow analyze/prefill failed", {
          error: String(err),
        });
        setDrawerState((prev) =>
          prev.open ? { ...prev, prefillLoading: false } : prev,
        );
      }
    },
    [
      applyAnalysisClient,
      emailContext,
      handleOpenPreLaunchForWorkflow,
      lastAttachmentText,
      runPrefillFetch,
    ],
  );

  const searchClients = useCallback(
    (query: string) => orchestratorApi.searchClientsLegacy(query),
    [],
  );

  // A pick made INSIDE the open drawer (did-you-mean / Change / search):
  // resolve the client, mark it confirmed, re-point the drawer at the new
  // clientSk, and re-run background prefill so the form reflects the newly
  // chosen client (cancel-ref + loading-flip pattern).
  // selectClientFromResult is async but already carries the picked result's
  // own clientName as a fallback when getClientDetails comes back empty (the
  // same helper the initial did-you-mean/search resolution already uses) —
  // seed drawerState.clientName from it here too so the name isn't left
  // blank until (or if) getClientDetails resolves. Previously this called
  // selectClient(clientSk) with only the numeric sk, so a client picked this
  // way (in-drawer Change/search) never got a name if getClientDetails
  // returned no client_name for it — confirmed live via instance
  // 254361c9, where client_sk landed in metadata.client_context but
  // client_name stayed null despite the AM having just picked "Sambasta"
  // from this exact search dropdown.
  const handleClientPicked = useCallback(
    (result: ClientSearchResult) => {
      clientCtx.selectClientFromResult(result);
      const entry = drawerState.catalogEntry;
      if (!entry) return;
      prefillCancelRef.current.cancelled = true;
      const cancel = { cancelled: false };
      prefillCancelRef.current = cancel;
      setDrawerState((prev) =>
        prev.open
          ? {
              ...prev,
              clientSk: result.clientSk,
              clientName: result.clientName,
              prefillLoading: true,
            }
          : prev,
      );
      // A brief opened from the list for another email: the selected item's
      // body and extraction are not this brief's, so re-run on its identity.
      const briefEmail = drawerState.briefEmail;
      if (briefEmail) {
        runBackgroundPrefill(
          entry,
          result.clientSk,
          "",
          {},
          null,
          cancel,
          briefEmail.internetMessageId,
          briefEmail.mailboxAddress,
        );
        return;
      }
      runBackgroundPrefill(
        entry,
        result.clientSk,
        emailContext?.body ?? "",
        stripExtractionMeta(lastExtraction),
        lastAttachmentText,
        cancel,
        emailContext?.internetMessageId,
      );
    },
    [clientCtx, drawerState.catalogEntry, drawerState.briefEmail, emailContext, lastExtraction, lastAttachmentText, runBackgroundPrefill],
  );

  // ── RE-ANALYSE (host half) ────────────────────────────────────────────────
  //
  // Two calls in a fixed order (PAD-4), and the order is the whole point:
  //
  //  1. `reanalyzeEmail` discards the STORED analysis and re-derives it. Skipping
  //     it and calling `reanalysePrefill` alone would re-run prefill against the
  //     extraction this pane is still holding — which the substrate treats as
  //     known-field-wins — so the recomputed brief would be overwritten by the
  //     very values the AM pressed the button to get rid of.
  //  2. `reanalysePrefill` — the SAME prefill run as `triggerPrefill` with a
  //     FRESH idempotency key. Without the fresh key the hashed key returns the
  //     prior COMPLETED instance and the button changes nothing while looking
  //     like it worked.
  //
  // `prefillLoading: true` is flipped BEFORE the fetch — the copy of
  // `handleClientPicked` above. `runPrefillFetch` only ever writes `false`, so
  // without the flip the drawer's true→false merge effect never fires and the
  // fresh brief never reaches `values` (R13).
  //
  // This callback owns NO form state: `LaunchForm` drops `values[actionsKey]`
  // on success so its create-rule rebuilds one action from the merged brief.
  const handleReanalyse = useCallback(async (): Promise<ReanalyseResult> => {
    const entry = drawerState.catalogEntry;
    if (!entry) return { ok: false, error: "No workflow is open to re-analyse." };
    // A brief opened from the list re-analyses ITS email, never the selected
    // item. reanalyze_email backfills subject / sender / Graph id from the
    // stored row and analyze_email fetches the body over Graph, so the
    // identity alone is a complete request.
    const briefEmail = drawerState.briefEmail;
    // Same fallback chain as the drawer's `internetMessageId` prop and
    // `buildAnalyzeEmailPayload`: the cache row is keyed on whichever of these
    // the host surfaced.
    const messageId = briefEmail
      ? briefEmail.internetMessageId
      : emailContext?.internetMessageId
        || emailContext?.restId
        || emailContext?.emailId
        || null;
    if (!messageId) {
      return {
        ok: false,
        error:
          "Open this from a mail item — re-analysis needs the email's id to "
          + "find the stored analysis.",
      };
    }
    prefillCancelRef.current.cancelled = true;
    const cancel = { cancelled: false };
    prefillCancelRef.current = cancel;
    setDrawerState((prev) =>
      prev.open ? { ...prev, prefillLoading: true } : prev,
    );
    const mailboxAddress = briefEmail
      ? briefEmail.mailboxAddress
      : resolveTriageMailbox(emailContext ?? {});
    try {
      const analysis = await orchestratorApi.reanalyzeEmail(
        messageId,
        mailboxAddress,
        briefEmail
          ? null
          : {
              email_subject: emailContext?.subject,
              email_sender: emailContext?.sender,
              email_body: emailContext?.body,
              email_rest_id: emailContext?.restId,
            },
      );
      const fresh = (analysis?.extraction ?? null) as
        | Record<string, unknown>
        | null;
      const clientSk = applyAnalysisClient(
        analysis,
        briefEmail ? null : emailContext?.emailId || null,
      );
      if (clientSk != null) {
        setDrawerState((prev) =>
          prev.open
            ? {
                ...prev,
                clientSk,
                clientName:
                  analysis.client_context?.client?.client_name ?? prev.clientName,
              }
            : prev,
        );
      }
      await runPrefillFetch(
        entry,
        clientSk,
        briefEmail ? "" : emailContext?.body ?? "",
        stripExtractionMeta(fresh),
        // `lastAttachmentText` is the selected item's — never a brief email's.
        briefEmail
          ? analysis?.attachment_text ?? null
          : analysis?.attachment_text ?? lastAttachmentText,
        cancel,
        true,
        messageId,
        briefEmail?.mailboxAddress,
      );
      return { ok: true };
    } catch (err) {
      // Reported, not thrown: the control states the failure and the previous
      // values stay on the form. An emptied form reads as "re-analysed, found
      // nothing", which is the silent-success class this control removes.
      setDrawerState((prev) =>
        prev.open ? { ...prev, prefillLoading: false } : prev,
      );
      logger.warn("App: re-analysis failed", { error: String(err) });
      return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
  }, [
    applyAnalysisClient,
    drawerState.catalogEntry,
    drawerState.briefEmail,
    emailContext,
    lastAttachmentText,
    runPrefillFetch,
    sharedMailboxTarget.targetMailbox,
  ]);

  // ── OPEN EMAIL (the launch form's hop back to the brief's email) ─────────
  // Offered only while the form's email is NOT the selected item — see
  // `outlook/sourceEmail.ts`.
  const sourceEmail = drawerState.sourceEmail;
  const openSourceEmail = useMemo(
    () =>
      sourceEmail && !isSelectedEmail(sourceEmail, emailContext)
        ? () => openMessageInOutlook(sourceEmail.graphMessageId)
        : undefined,
    [sourceEmail, emailContext],
  );

  // The launch form's client follows the client the pane SHOWS: a client that
  // resolves after the drawer opened (the per-email identify run on a reopened
  // email, a lookup by name) is adopted by a form that has none, and a pick's
  // canonical name back-fills. Rules (incl. never adopting another email's
  // client for a brief) in syncDrawerClient — story 28633.
  useEffect(() => {
    const client = clientCtx.clientContext?.client;
    if (!client) return;
    setDrawerState((prev) => syncDrawerClient(prev, client));
  }, [clientCtx.clientContext]);

  // ── New E2E path: CTA button opens the drawer with email-field prefill ────
  const handleLaunchRecommended = useCallback(
    (entry: WorkflowCatalogEntry, emailPrefill: Record<string, unknown>) => {
      // Fall back to the raw extraction name so inputs.client_name reaches the
      // substrate even when the name lookup didn't resolve to a single match.
      const clientPrefill: Record<string, unknown> = clientCtx.clientContext
        ? {
            client_sk: clientCtx.clientContext.client.client_sk,
            client_name: clientCtx.clientContext.client.client_name,
          }
        : lastExtraction?.client_name
          ? { client_name: lastExtraction.client_name as string }
          : {};
      // Email-derived fields win; client fields fill the remaining keys.
      // Clientless CTA opens quarantine the client-derived keys — same
      // ordering fix as the background-prefill merge.
      const prefill: Record<string, unknown> = {
        ...clientPrefill,
        ...(clientCtx.clientContext
          ? emailPrefill
          : quarantineClientDerivedPrefill(emailPrefill)),
      };
      setDrawerState({
        open: true,
        mode: "pre_launch",
        workflowRef: entry.ref,
        catalogEntry: entry,
        prefill,
        clientSk: clientCtx.clientContext?.client.client_sk ?? null,
        clientName: clientCtx.clientContext?.client.client_name ?? null,
      });
    },
    [clientCtx.clientContext, lastExtraction],
  );

  // NOTE (2026-07-21): the old handlePolicyChosen server re-trigger is GONE.
  // Picking a policy used to re-run workflow_prefill with the chosen number
  // threaded so the server could resolve carrier_name — obsolete now that the
  // enriched policy_options rows carry carrier/type and the forced-selection
  // combobox writes them locally on pick. The email is processed exactly once;
  // a policy switch never re-runs extraction (which would also clobber the
  // user's manual field edits via the prefill merge).

  const handleOpenWorkflow = useCallback(
    (instanceId: string, workflowId: string, catalogEntry?: WorkflowCatalogEntry) => {
      const entry = catalogEntry ?? catalogMapRef.current.get(workflowId);
      setDrawerState({
        open: true,
        mode: "live",
        instanceId,
        workflowId,
        catalogEntry: entry,
        // Do NOT seed clientSk/clientName from email context here — the workflow
        // may belong to a different client than the currently-open email. The
        // drawer derives client identity from the workflow's own status payload.
        clientSk: null,
        clientName: null,
      });
    },
    [],
  );

  // Bumped on launch/retry so the context strip re-reads the selected email.
  // The launched email usually stays selected, so no ItemChanged fires and the
  // strip kept offering Review & Approve over a running run (DEV, 2026-09-28).
  // One re-read is enough: the start call returns after the run row is written.
  const [contextStripRefreshSignal, setContextStripRefreshSignal] = useState(0);

  const handleDrawerLaunched = useCallback(
    (result: { instance_id: string; workflow_id: string; idempotency_key: string }) => {
      setDrawerState((prev) => ({
        ...prev,
        open: true,
        mode: "live",
        instanceId: result.instance_id,
        workflowId: result.workflow_id,
        idempotencyKey: result.idempotency_key,
      }));
      setContextStripRefreshSignal((n) => n + 1);
      // Stay on Needs you. The live drawer is the run; flipping to Active
      // is the old In flight / All workflows list.
    },
    [],
  );

  const handleDrawerRetried = useCallback(
    (result: { instance_id: string; workflow_id: string; idempotency_key: string }) => {
      setDrawerState((prev) => ({
        ...prev,
        mode: "live",
        instanceId: result.instance_id,
        workflowId: result.workflow_id,
        idempotencyKey: result.idempotency_key,
      }));
      setContextStripRefreshSignal((n) => n + 1);
    },
    [],
  );

  const handleDrawerClose = useCallback(() => {
    prefillCancelRef.current.cancelled = true;
    // 26747: closing a brief closes the `brief_review` pair. A brief that was
    // LAUNCHED leaves through here too — the drawer flips to `live` first, so
    // the mode read below is what tells the two apart.
    setDrawerState((prev) => {
      if (prev.open && prev.mode === "pre_launch") {
        emitTouchpoint("brief_review", "leave", {
          workflow_id: prev.workflowId ?? null,
        });
      }
      return prev;
    });
    // reuseIdentity is cleared here, not merely on the next open: leaving it
    // set would let a later catalog-open launch inherit a finished run's email
    // identity and silently RE-ANALYSE the wrong message.
    setDrawerState((prev) => ({
      ...prev,
      open: false,
      reuseIdentity: null,
      briefEmail: null,
    }));
    // The strip may have changed while the drawer was open (an acknowledged
    // warning flips Completed* to Completed): re-read it.
    setContextStripRefreshSignal((n) => n + 1);
  }, []);

  // Where the running view's 5s countdown lands. It closes the DRAWER and
  // returns the pane to its landing page — never `Office.addin.hide()`, which
  // would dismiss a pinned pane the Servicer pinned precisely so it would
  // stay. "needs_you" is that landing page (story pinned-pane, AC-2), and it
  // is the same gesture an ora notification already uses to bring the pane
  // back to the queue.
  //
  // A MANUAL close does NOT do this: it leaves the Servicer on the tab they
  // were on, because they are watching and the pane does not get to move them.
  const handleDrawerAutoClose = useCallback(() => {
    setCurrentTab("needs_you");
    handleDrawerClose();
  }, [handleDrawerClose]);

  // ── running-drawer AC-9: reuse the submitted request ──────────────────────
  // OPENS a pre-filled launch surface and starts NOTHING: no triggerWorkflow,
  // no cancelInstance, no mutation of the run being viewed. The drawer is
  // re-keyed on drawerOpenCount so the pre_launch form REMOUNTS with the reused
  // values rather than merging them into stale ones.
  const handleReuseSubmittedRequest = useCallback(
    (values: Record<string, unknown>, identity: string | null) => {
      setDrawerState((prev) => {
        if (!prev.catalogEntry) return prev;
        return {
          open: true,
          mode: "pre_launch",
          workflowRef: prev.workflowRef,
          catalogEntry: prev.catalogEntry,
          prefill: values,
          prefillLoading: false,
          clientSk: prev.clientSk ?? null,
          clientName: prev.clientName ?? null,
          reuseIdentity: identity,
        };
      });
      setDrawerOpenCount((c) => c + 1);
    },
    [],
  );

  // ── The Servicer clicked ora's own notification (story 26738) ─────────────
  // The route that works with no admin involvement at all. With the pane
  // pinned, selecting the alert in the message list is the whole gesture:
  // Outlook hands the pane the new item, `useMailboxItem` reads ora's
  // `X-Ora-Instance-Id` off it, and the waiting decision opens here.
  //
  // Verified to survive delivery on 2026-09-06 against the raw MIME, and again
  // on 2026-09-24 against an INJECTED notification (ADO 28182): the header is
  // present both in Graph's `internetMessageHeaders` and in
  // PR_TRANSPORT_MESSAGE_HEADERS, which is what `getAllInternetHeadersAsync`
  // actually reads. Injection does not lose it.
  //
  // The card button in the body is the same landing by a different door, and it
  // is the one still blocked -- it needs a registered Actionable Messages
  // originator (26722). NOT because Exchange strips it: that earlier claim was
  // wrong, and was based on the card being missing from a message read back
  // through Graph, which sanitises <script> out of every body it returns.
  //
  // REQUIRES MAILBOX 1.8. The manifest floor is 1.5, so on an older host
  // `getAllInternetHeadersAsync` does not exist, `readOraInstanceId` resolves
  // "" and this effect never fires. That is a silent no-op by design -- but it
  // is also the first thing to check when the pane does not open from an
  // alert.
  //
  // KEYED ON THE SELECTED MESSAGE, NOT ON THE RUN.
  //
  // It used to remember the last INSTANCE it had opened, which made the alert a
  // one-shot: open the run once, and clicking that alert again for the rest of
  // the pane's life did nothing (reported 2026-09-25 — "it opened the run the
  // first time I clicked the email, but not the subsequent clicks"). The ref
  // was never cleared, so selecting another message and coming back still
  // matched the remembered instance and returned early.
  //
  // Remembering the MESSAGE instead gets both halves right. The ref is updated
  // for every message the Servicer selects, ora's or not, so:
  //
  //   * re-render while the same message stays selected -> same id, no re-open,
  //     which is what stops a drawer the Servicer just closed springing back;
  //   * select anything else and return -> the ref now holds that other
  //     message, so the alert opens its run again, every time;
  //   * two alerts for the SAME run -> different messages, so the second one
  //     still opens, which the instance key also got wrong.
  //
  // The ref is written BEFORE the ora check, deliberately: a non-ora message
  // must still count as "the selection moved", or returning to the alert would
  // be indistinguishable from never having left it.
  const lastAutoOpenedEmailRef = useRef<string | null>(null);
  useEffect(() => {
    const emailId = emailContext?.emailId ?? "";
    if (!emailId || lastAutoOpenedEmailRef.current === emailId) return;
    lastAutoOpenedEmailRef.current = emailId;
    const instanceId = emailContext?.oraInstanceId ?? "";
    if (!instanceId) return;
    logger.info("addin.opened_from_ora_notification", { instanceId });
    setCurrentTab("needs_you");
    handleOpenWorkflow(instanceId, "");
  }, [emailContext?.emailId, emailContext?.oraInstanceId, handleOpenWorkflow]);

  // ── Opened from an ora notification's card button (story 26738) ───────────
  // The pane can be opened by `Action.InvokeAddInCommand` on the Actionable
  // Message in a HITL email. When it is, Outlook hands over the instance the
  // card was about, and the pane opens THAT run rather than the Needs You list
  // — which is the whole difference between the notification landing on the
  // decision (SC-3) and landing near it.
  //
  // Runs ONCE, and only when a context arrives. Every ribbon open — the common
  // case by far — resolves null and changes nothing. `workflowId` is left empty
  // deliberately: the card carries the run, and the live drawer derives the
  // workflow from the run's own status payload.
  const invokeContextHandledRef = useRef(false);
  useEffect(() => {
    if (invokeContextHandledRef.current) return;
    invokeContextHandledRef.current = true;
    let cancelled = false;
    void readInitializationContext().then((context) => {
      if (cancelled || !context) return;
      logger.info("addin.invoked_from_notification", {
        instanceId: context.instanceId,
        decisionKey: context.decisionKey || null,
      });
      setCurrentTab("needs_you");
      handleOpenWorkflow(context.instanceId, "");
    });
    return () => {
      cancelled = true;
    };
  }, [handleOpenWorkflow]);

  // ── Sign-out handler ──────────────────────────────────────────────────────
  const handleSignOut = useCallback(() => {
    resetWorkflow();
    clientCtx.reset();
    setItemAnalysis(null);
  }, [resetWorkflow, clientCtx]);

  // ─────────────────────────────────────────────────────────────────────────
  /** The catalog for the pane's own Launch surface. The full-frame "Choose a
   *  workflow" takeover that also mounted it is gone (story 28614): Launch
   *  anyway opens the context strip's Start a workflow picker instead. */
  const launchCatalog = (
    <LaunchTab
      onOpenCatalog={handleOpenCatalog}
      onOpenWorkflow={handleOpenWorkflow}
      demoMode={demoMode}
      clientCtx={clientCtx}
      emailContext={emailContext}
      isLiveConnected={isConnected}
      autoSuggestedWorkflowRef={autoSuggestedRef}
      onClearSuggestion={() => setAutoSuggestedRef(null)}
      onSuggestion={(id) => {
        setAutoSuggestedRef(canonicalRecommendedWorkflowId(id) ?? id);
      }}
      onMetricsUpdate={setKpiMetrics}
      onLaunchRecommended={handleLaunchRecommended}
      activeInstanceId={drawerState.open && drawerState.mode === "live" ? drawerState.instanceId : undefined}
      onCountChange={setActiveCount}
      sharedMailboxTarget={sharedMailboxTarget}
      catalogFromHost={launchCatalogSource}
    />
  );

  return (
    <AppShell
      demoMode={demoMode}
      onToggleDemo={() => {
        const next = !demoMode;
        setDemoMode(next);
        localStorage.setItem("ora_demo_mode_addin", String(next));
      }}
      currentTab={paneTab}
      onTabChange={setCurrentTab}
      activeCount={activeCount}
      needsYouCount={needsYouCount}
      onSignOut={handleSignOut}
    >
      {/* ── Per-email context strip ──────────────────────────────────────────
          Mounted ABOVE the tab content, deliberately: whichever email the AM
          clicks, what the automation already knows about it must be readable
          without scrolling the queue away (AC-4/AC-5). This is the mount site
          — the strip exists nowhere else in the tree. */}
      <ContextStripHost
        emailContext={emailContext}
        onRunAnalysis={handleProcess}
        onOpenRun={handleOpenWorkflow}
        onOpenPreLaunch={handleOpenPreLaunchForWorkflow}
        onStartSelectedWorkflow={handleStartSelectedWorkflow}
        catalog={catalogEntries}
        refreshSignal={contextStripRefreshSignal}
        notice={launchNotice}
      />

      {/* SignalR unavailable banner */}
      {isSignalRUnavailable && (
        <div className="mx-4 mb-2 rounded-md bg-primary-warning-50 border border-primary-warning-300 px-3 py-[6px] text-xs text-primary-warning-700">
          Live updates unavailable — polling every 5s.
        </div>
      )}

      {/* ── Needs-you tab (queue first) ─────────────────────────────────── */}
      {paneTab === "needs_you" && (
        <NeedsYouTab
          onOpenItem={handleOpenWorkflow}
          onCountChange={setNeedsYouCount}
          currentMailboxAddress={emailContext?.mailboxAddress}
          currentMessageId={emailContext?.internetMessageId ?? null}
          refreshSignal={needsYouRefreshSignal}
          catalog={catalogEntries}
          onOpenBrief={(item) => {
            void handleOpenPreLaunchForWorkflow(item.recommended_workflow_id, {
              internetMessageId: item.internet_message_id,
              // Story 26727: `mailbox_address` is the triage row's own key;
              // `source_mailbox` (provenance) is the fallback for a row from
              // before the widened key.
              mailboxAddress: item.mailbox_address ?? item.source_mailbox,
              clientName: item.client_name,
              clientSk: item.client_sk,
              clientCity: item.client_city,
              clientState: item.client_state,
              clientBranch: item.client_branch,
              policyNumber: item.policy_number,
              prefillValues: item.prefill_values,
              fieldConfidence: item.field_confidence,
              triageOutcome: item.triage_outcome,
              graphMessageId: item.graph_message_id,
            });
          }}
        />
      )}

      {/* ── Launch tab ──────────────────────────────────────────────────── */}
      {paneTab === "launch" && (
        <div className="relative flex-1">
          {/* Always-visible launch catalog view */}
          {launchCatalog}

          {/* Legacy auto-analyze state machine overlay — rendered OVER the
              LaunchTab when the email-analysis flow is active.
              Shown when phase is NOT idle/client_context (those are either
              done or auto-advancing). */}
          {phase !== "idle" && phase !== "client_context" && (
            <div className="absolute inset-0 overflow-auto bg-primary-gray-light-50 p-4">
              {phase === "authenticating" && <AuthProgress />}

              {phase === "analyzing" && (
                <div className="flex flex-col gap-4">
                  <div>
                    <h1 className="text-display-xs text-primary-gray-light-900">
                      Analyzing email
                    </h1>
                    <p className="flex items-center gap-1.5 text-sm text-primary-gray-light-600 pt-[0.438rem] animate-pulse">
                      <Spinner size={14} />
                      Identifying client and recommended workflow…
                    </p>
                  </div>
                  {kpiMetrics && kpiMetrics.total > 0 ? (
                    <div className="flex gap-[10px]">
                      {[
                        { key: "total", value: kpiMetrics.total.toLocaleString(), suffix: null, label: "Automated" },
                        { key: "hours", value: kpiMetrics.hours_saved.toLocaleString(), suffix: "hrs", label: "Saved" },
                      ].map(({ key, value, suffix, label }) => (
                        <div
                          key={key}
                          className="flex-1 rounded-[10px] border border-primary-gray-light-200 bg-base-white p-4 text-center"
                        >
                          <div className="text-display-xs font-bold leading-none text-primary-gray-light-900">
                            {value}
                            {suffix && (
                              <span className="ml-[3px] text-md font-semibold text-primary-gray-light-500">
                                {suffix}
                              </span>
                            )}
                          </div>
                          <div className="mt-[6px] text-xs font-medium text-primary-gray-light-500">
                            {label}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : kpiMetrics === null ? (
                    <div className="flex gap-[10px]">
                      <Skeleton className="h-[68px] flex-1" />
                      <Skeleton className="h-[68px] flex-1" />
                    </div>
                  ) : null}
                </div>
              )}

              {phase === "processing" && (
                <StepProgress workflowId={workflowId} status={status} />
              )}

              {phase === "plan_review" && plan && (
                <PlanReviewPanel
                  plan={plan}
                  onApprove={async (corrections) => {
                    await approvePlan(corrections);
                  }}
                  onReject={async (reason) => {
                    await rejectPlan(reason);
                  }}
                />
              )}

              {phase === "executing" && (
                <StepProgress workflowId={workflowId} status={status} />
              )}

              {phase === "completed" && (
                <StepProgress
                  workflowId={workflowId}
                  status={status}
                  completed
                  artifacts={artifacts}
                />
              )}

              {phase === "failed" && (
                <StepProgress
                  workflowId={workflowId}
                  status={status}
                  failed
                  authFailed={failureContext === "auth"}
                />
              )}

              {/* Back button — exits the legacy overlay back to catalog */}
              {(phase === "failed" || phase === "completed") && (
                <button
                  type="button"
                  onClick={resetWorkflow}
                  className="mt-4 text-xs text-primary-gray-light-500 hover:text-primary-gray-light-700 underline"
                >
                  ← Back to catalog
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Active tab retired: Needs you is the pane. Running hops open the
          live drawer instead of flipping to In flight / All workflows. */}

      {/* ── InstanceDetailDrawer ─────────────────────────────────────────── */}
      <InstanceDetailDrawer
        key={`${drawerState.workflowRef ?? "drawer"}-${drawerOpenCount}`}
        open={drawerState.open}
        onClose={handleDrawerClose}
        onAutoClose={handleDrawerAutoClose}
        /* The drawer's only exit. It names the destination because the pane
           has no tab bar (the drawer covers it, height: 100vh). Only the host
           knows which destination it is. */
        onBack={handleDrawerClose}
        backLabel={
          drawerState.fromContextStrip ? "Back to this email" : "Back to your queue"
        }
        mode={drawerState.mode}
        catalogEntry={drawerState.catalogEntry}
        // The LAUNCH entry above is null on both notification landings (the
        // card carries the run, not a workflow id). The drawer resolves the
        // entry that describes the RUN from this list instead — without it the
        // shared Steps log has no declared manifest and stops reporting the
        // steps that never ran.
        catalog={catalogEntries}
        prefill={decomposedPrefill}
        fieldConfidence={drawerState.fieldConfidence}
        prefillLoading={drawerState.prefillLoading}
        graphMessageId={
          // A brief opened from the list replies to ITS email, not whichever
          // item happens to be selected (or none).
          drawerState.briefEmail
            ? drawerState.briefEmail.graphMessageId
            : emailContext?.emailId ?? null
        }
        onHitlResponded={handleHitlResponded}
        onReuse={handleReuseSubmittedRequest}
        internetMessageId={
          // running-drawer AC-9: a reuse carries the RUN's own identity so
          // RE-ANALYSE (prefill-drawer) re-derives from the email the run was
          // launched from. Absent that, the same fallback chain as
          // buildAnalyzeEmailPayload — the launch feedback loop looks up the
          // email_analysis cache row by this value, so it must equal the key
          // analyze_email cached under (restId when the host surfaces no
          // RFC 5322 Message-ID).
          drawerState.reuseIdentity
          || emailContext?.internetMessageId
          || emailContext?.restId
          || emailContext?.emailId
          || null
        }
        instanceId={drawerState.instanceId}
        clientSk={drawerState.clientSk}
        clientName={drawerState.clientName}
        previousIdempotencyKey={drawerState.idempotencyKey}
        onLaunched={handleDrawerLaunched}
        onRetried={handleDrawerRetried}
        // Only a launch the context strip started is a RE-RUN, so only that
        // one logs the refusal. The drawer's own refusal UI is unchanged
        // either way — this is observation, not behaviour.
        onLaunchRefused={
          drawerState.fromContextStrip ? logRerunRefused : undefined
        }
        renderClientSection={
          drawerState.mode === "pre_launch"
            ? () => (
                <ClientConfirmSection
                  details={clientCtx.clientContext}
                  pickConfidence={clientCtx.clientContext?.client_pick_confidence}
                  suggestions={clientCtx.suggestedResults}
                  loading={
                    !clientCtx.clientContext &&
                    (Boolean(drawerState.prefillLoading) ||
                      clientCtx.state === "searching" ||
                      clientCtx.state === "analyzing" ||
                      clientCtx.state === "matched" ||
                      clientCtx.state === "gathering")
                  }
                  onSelectClient={handleClientPicked}
                  searchClients={searchClients}
                />
              )
            : undefined
        }
        renderInputForm={({ schema, values, setValues, onSubmit, submitting, fieldConfidence, fieldLoading, signInSection, submitDisabledReason, prefillLoading, leadingContent, runLevelControls }) => {
          // The mortgagee-specific customization below (fieldErrors, fieldHidden,
          // fieldOverrides, etc.) hardcodes mortgagee field names (change_type,
          // mortgagees, old_mortgagee_name). renderInputForm is one render-prop
          // shared by every workflow's pre-launch form, so any OTHER workflow
          // (e.g. epic_smoke_test) must fall through to a plain, unopinionated
          // form — otherwise the hardcoded `change_type` fieldError below is
          // permanently truthy (the field doesn't exist on other schemas) and
          // Submit stays disabled no matter what the visible fields contain.
          if (!workflowRequiresClient(drawerState.catalogEntry)) {
            return (
              <>
                {leadingContent}
                {runLevelControls}
                <SchemaForm
                  schema={schema}
                  value={values}
                  onChange={setValues}
                  onSubmit={onSubmit}
                  submitting={submitting}
                  submitDisabledReason={submitDisabledReason}
                  fieldConfidence={fieldConfidence}
                  fieldLoading={fieldLoading}
                  preFooter={signInSection}
                />
              </>
            );
          }
          // ── The grouped form (this story) ─────────────────────────────────
          //
          // The switch is the DECLARED SCHEMA, never the workflow id: a schema
          // that declares an action list gets the grouped form, every other
          // catalog entry — `mortgagee_update_v1_2`, the workflow AMs launch
          // today, included — falls through to the flat branch below unchanged.
          // That is what makes this change unable to regress the pilot's live
          // launch path (R10).
          const binding = buildLaunchBinding(schema);
          if (binding.isReshaped) {
            return (
              <LaunchForm
                binding={binding}
                values={values}
                // The drawer's own `useState` setter, NOT a narrowing wrapper:
                // RE-ANALYSE's success drop is a functional update, and by the
                // time its promise settles the host has already merged the
                // fresh brief — a drop computed from a stale closure would
                // throw that merge away.
                setValues={setValues}
                onSubmit={onSubmit}
                submitting={submitting}
                submitDisabledReason={submitDisabledReason}
                // Threaded through the render-prop so the create-rule waits out
                // an in-flight background prefill. A first-paint create against
                // an empty list freezes an empty action the drawer's later
                // shallow merge cannot enter (R13).
                prefillLoading={prefillLoading}
                fieldConfidence={fieldConfidence}
                fieldLoading={fieldLoading}
                // Story 26752 AC-5: withheld (not merely disabled) once the
                // server has told us this caller lacks operator permission —
                // `undefined` (no analysis response yet) stays permissive; see
                // DrawerState.canReanalyze.
                onReanalyse={
                  drawerState.canReanalyze === false ? undefined : handleReanalyse
                }
                onOpenEmail={openSourceEmail}
                leadingContent={leadingContent}
                runLevelControls={runLevelControls}
                preFooter={signInSection}
                submitLabel="Approve & Run"
              />
            );
          }
          // Forced-selection policy pick (operator decision 2026-07-21): when
          // the server resolved the client's in-force policy_options, the
          // schema's free-text policy_number input is replaced by a combobox —
          // typing only filters; the committed value must be a selection. The
          // pick also DRIVES policy_type / carrier_name / carrier_id from the
          // same row (those fields lock), and effective_date must fall within
          // the chosen policy's term.
          //
          // FLAT ONLY from here down. Everything below runs after the
          // `binding.isReshaped` early return above, so the values record is
          // one level deep by construction — `mortgagee_update_v1_2`, the
          // workflow AMs launch today, and any other client-gated flat schema.
          // The per-level branching this block used to carry (`actionOf` /
          // `applyLaunchPatch` against `schemaHasActionList`) was
          // `action-list-contract`'s interim bridge for the window in which one
          // render-prop served both shapes; `LaunchForm` serves the reshaped
          // one now, so the branch is unreachable and its module is retired.
          // `action` survives as a NAME for "the record the per-action reads
          // come from", which on this branch is the values record itself.
          const action: Record<string, unknown> = values;
          const writePatch = (patch: Record<string, unknown>) => ({
            ...values,
            ...patch,
          });
          const policyOptions = readPolicyOptions(values);
          const hasPolicyOptions = policyOptions.length >= 1;
          // Interest target picker (declared-change-type-prefill).
          // A policy number is NOT a policy line: one number can be a current
          // term AND its future renewal, each with its own line and its own
          // mortgagees. The substrate emits per-line options, so the options on
          // offer are the ones belonging to the term the AM actually picked —
          // resolved server-side-consistently inside this helper, never
          // re-derived here. Falls back to the older single-line payload when
          // the deployed substrate predates that key.
          const interestOptions = readInterestOptionsForSelection(values);
          // Absent policy selection -> no picker at all. Absent options
          // (UNKNOWN read, or a term with no mortgagee on file) -> render
          // nothing and leave old_mortgagee_name editable, i.e. exactly
          // today's behaviour.
          //
          // >= 1, not >= 2 (operator correction 2026-08-03): with the
          // target_interest field gone, the picker's popout is the ONLY
          // place the interest's grid details surface, and the name must
          // match EPIC exactly — a single-option policy still needs the
          // derived control, never a free-text default. One option renders
          // a one-row dropdown, already selected when the server resolved
          // the target.
          const showInterestPicker =
            !!action.policy_number && interestOptions.length >= 1;
          const currentTarget =
            (action.target_interest as InterestOption | null | undefined) ?? null;
          // Progressive disclosure (form redesign 2026-08-03): the action
          // group opens with ONLY the Add/Change/Delete radios; the pick
          // reveals what that action needs. Values are never deleted by
          // hiding — a hidden field still validates and still submits (see
          // fieldHidden's contract), which is what keeps the wire payload
          // identical to the pre-redesign form.
          const changeType =
            typeof action.change_type === "string" ? action.change_type : "";
          // PolicyNumberCombobox — the SAME widget either way; only the ADDRESS
          // the renderer looks it up at changes. `fieldOverrides` matches
          // TOP-LEVEL FieldRow keys only, so once `policy_number` lives inside
          // `actions[]` this has to move to
          // `itemFieldOverrides["actions.policy_number"]` — otherwise the AM
          // gets the schema's plain free-text input back and the
          // forced-selection policy pick silently stops being forced.
          const policyNumberOverride = ({
            field,
            value,
            readOnly,
          }: {
            field: { label: string; required: boolean };
            value: unknown;
            readOnly: boolean;
          }) => (
            <PolicyNumberCombobox
              density="compact"
              label={field.label}
              required={field.required}
              value={typeof value === "string" ? value : ""}
              termKey={readPolicyTermKey(values)}
              options={policyOptions}
              disabled={readOnly || submitting}
              onSelect={(opt) => {
                // The selected row carries everything the pick drives (type +
                // carrier) — no server re-trigger. The email was already
                // processed once; a policy switch must not re-run extraction
                // (it would also clobber the user's manual field edits).
                const group = opt.product_group ?? opt.policy_type;
                // ONE mixed patch through writePatch: `policy_number` and
                // `target_interest` moved into the action, while `policy_type`
                // / `carrier_name` / `carrier_id` / the term key /
                // `old_mortgagee_name` stayed run-level.
                //
                // Interests are per-term, so a target resolved against the
                // previously selected term cannot describe this one — carrying
                // it across would submit another term's mortgagee row, and now
                // that a switched term offers its own options it would also be
                // what the picker displays. The reset also drops
                // old_mortgagee_name when it was DERIVED from that target
                // rather than typed/extracted; see interestResetPatch.
                const next = writePatch({
                  ...interestResetPatch(values),
                  policy_number: opt.policy_number ?? "",
                  // WHICH term was picked — the number alone can't say when a
                  // current term and its future renewal share it, and every
                  // downstream consumer (selection, effective-date validation,
                  // the launch gate) resolves the row from this key.
                  [POLICY_TERM_KEY_FIELD]: policyTermKey(opt),
                  ...(group ? { policy_type: group } : {}),
                  ...(opt.carrier_name ? { carrier_name: opt.carrier_name } : {}),
                  ...(opt.carrier_id ? { carrier_id: opt.carrier_id } : {}),
                });
                // Computed against the switched-TO term: when it carries
                // exactly one mortgagee and the reset left the field
                // untouched, pre-select it — the same rule the server applies
                // to the term IT resolved.
                // FLAT: `next` is already the whole record, so the auto-pick
                // patch spreads straight onto it. (`writePatch` closes over
                // `values`, not `next`, so it must not be reused here.)
                setValues({ ...next, ...interestAutoPickPatch(next) });
              }}
              onClear={() =>
                setValues(
                  writePatch({
                    // Same reason as onSelect: no policy means no term, so a
                    // per-term target cannot still apply.
                    ...interestResetPatch(values),
                    policy_number: "",
                    [POLICY_TERM_KEY_FIELD]: "",
                  }),
                )
              }
            />
          );
          return (
            <>
            {leadingContent}
            {runLevelControls}
            <SchemaForm
              // Presentation-only rewrite: the mortgagee item's single
              // mailing_address renders as Street / City / State / ZIP
              // inputs (split-address form 2026-08-03). The wire schema is
              // untouched — onSubmit composes the canonical string back.
              schema={splitAddressSchema(schema)}
              value={values}
              onChange={setValues}
              // Compose "<street>, <city>, <ST> <zip>" per mortgagee and
              // strip the part keys — the payload shape is byte-identical
              // to the pre-split form, and the composed string matches the
              // workflow's regex parser BY CONSTRUCTION.
              onSubmit={() => onSubmit(composeMortgageeValues(values))}
              submitting={submitting}
              submitDisabledReason={submitDisabledReason}
              fieldConfidence={fieldConfidence}
              fieldLoading={fieldLoading}
              fieldOverrides={
                hasPolicyOptions || showInterestPicker
                  ? {
                      ...(showInterestPicker
                        ? {
                            old_mortgagee_name: ({ field, readOnly }) => (
                              <OldMortgageeSelect
                                field={field}
                                options={interestOptions}
                                target={currentTarget}
                                name={
                                  typeof values.old_mortgagee_name === "string"
                                    ? values.old_mortgagee_name
                                    : ""
                                }
                                disabled={readOnly || submitting}
                                onSelect={(opt) =>
                                  // The whole row, already snake_case — see
                                  // normalizeInterestOption. Writing the raw
                                  // camelCase row here would silently blank
                                  // four of the five fields the EPIC match
                                  // compares on, with no error anywhere.
                                  // target_interest no longer renders as a
                                  // field (its details live in this control's
                                  // popout) but MUST keep travelling on submit
                                  // — and it now travels INSIDE actions[0],
                                  // which is what writePatch resolves.
                                  setValues(
                                    writePatch({
                                      target_interest: opt,
                                      old_mortgagee_name: opt.name,
                                    }),
                                  )
                                }
                                onNotListed={() =>
                                  setValues(
                                    writePatch({
                                      target_interest: null,
                                      old_mortgagee_name: "",
                                    }),
                                  )
                                }
                                onManualChange={(name) =>
                                  setValues(
                                    writePatch({
                                      target_interest: null,
                                      old_mortgagee_name: name,
                                    }),
                                  )
                                }
                              />
                            ),
                          }
                        : {}),
                      policy_number: policyNumberOverride,
                    }
                  : undefined
              }
              fieldDisabled={
                hasPolicyOptions
                  ? {
                      policy_type: true,
                      carrier_name: true,
                      carrier_id: true,
                    }
                  : undefined
              }
              fieldHidden={{
                // Never a form field anymore: the picked row's grid details
                // surface inside OldMortgageeSelect's popout. The VALUE still
                // travels — the onSelect above writes it into `values`.
                target_interest: true,
                // No action picked yet → only the radios show.
                ...(changeType === ""
                  ? { old_mortgagee_name: true, mortgagees: true }
                  : {}),
                // Add: there is no OLD mortgagee to pick.
                ...(changeType === "add" ? { old_mortgagee_name: true } : {}),
                // Delete asks for no new mortgagee. The control hides; the
                // VALUE must still be sent — an omitted key is rejected
                // server-side whereas [] validates, which is why this only
                // hides and never deletes from `values`.
                ...(changeType === "delete" ? { mortgagees: true } : {}),
              }}
              // Editable-first, read-only-last (operator redesign
              // 2026-08-03). The schema keeps its LLM-authored order; this
              // is presentation only. Every entry is a key that may or may not
              // exist — an unlisted or absent key just keeps schema order.
              fieldOrder={[
                "policy_number",
                "effective_date",
                "change_type",
                "old_mortgagee_name",
                "mortgagees",
                "policy_type",
                "carrier_name",
              ]}
              // One bordered group per mortgagee ACTION — deliberately shaped
              // so compound requests (add one + delete another) later become
              // "repeat this section with a + button", not a re-layout.
              fieldSections={[
                {
                  title: "Mortgagee action",
                  fields: [
                    "change_type",
                    "old_mortgagee_name",
                    "target_interest",
                    "mortgagees",
                  ],
                  testId: "mortgagee-action-section",
                },
              ]}
              // The schema descriptions are LLM-facing Pydantic field docs and
              // read as wall-of-text in a narrow task pane — suppress them here
              // (operator request 2026-07-28) rather than editing the workflow
              // input model, which would also degrade extraction + MCP docs.
              hideFieldDescriptions
              fieldNotes={{
                effective_date:
                  "If effective date is left blank it will fallback to the effective date on the policy selected.",
              }}
              itemFieldOverrides={{
                // State + ZIP share one line (operator request 2026-08-03):
                // this override renders BOTH controls in a two-column grid
                // and writes both keys through the row's setter; the
                // mailing_zip override below suppresses the default ZIP row
                // so the field isn't rendered twice.
                "mortgagees.mailing_state": ({ field, value, setValue, readOnly, index, item }) => (
                  <div className="grid grid-cols-2 gap-2">
                    {/* Filterable "Michigan | MI" combobox — commits only
                        valid USPS codes, so the composed address always
                        parses. */}
                    <StateSelect
                      label={field.label}
                      value={typeof value === "string" ? value : ""}
                      disabled={readOnly || submitting}
                      onCommit={(code) => setValue(field.key, code)}
                      idSuffix={String(index)}
                    />
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor={`mortgagee-zip-${index}`}
                        className="flex items-center gap-1.5 text-xs font-bold text-primary-gray-light-700"
                      >
                        ZIP
                      </label>
                      <input
                        id={`mortgagee-zip-${index}`}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={
                          typeof item.mailing_zip === "string"
                            ? item.mailing_zip
                            : ""
                        }
                        disabled={readOnly || submitting}
                        onChange={(e) => setValue("mailing_zip", e.target.value)}
                        className="w-full rounded-lg border border-primary-gray-light-300 px-3 py-2 text-sm bg-base-white text-primary-gray-light-900 disabled:bg-primary-gray-light-50 disabled:text-primary-gray-light-700"
                        data-testid={`mortgagee-zip-${index}`}
                      />
                    </div>
                  </div>
                ),
                "mortgagees.mailing_zip": () => null,
              }}
              // Keyed by the BARE field name either way: `ArrayOfObjectsField`
              // falls back to the bare key for a sub-field, so these keep
              // rendering (and keep blocking Submit) after the fields moved
              // one level down.
              fieldErrors={{
                effective_date: validateEffectiveDateAgainstPolicy(
                  action.effective_date,
                  policyOptions,
                  typeof action.policy_number === "string"
                    ? action.policy_number
                    : null,
                  readPolicyTermKey(values),
                ),
                // No action, no launch (operator correction 2026-08-03): the
                // wire field is optional (legacy launches omit it), so the
                // schema's required-check can't enforce this — the FORM must.
                // An unset action would send change_type "" and the run would
                // behave like a legacy/undeclared launch, skipping every
                // declared-intent gate the AM thinks they're getting.
                change_type:
                  changeType === ""
                    ? "Select Add, Change, or Delete to continue."
                    : null,
                // All-or-nothing per row: a partially-filled address blocks
                // Submit with a message instead of launching a run that
                // would end in a cleared record. Only enforced while the
                // mortgagees section is VISIBLE — a hidden field's error
                // would block Submit with nothing on screen to fix.
                mortgagees:
                  changeType === "add" || changeType === "update"
                    ? mortgageeAddressError(values)
                    : null,
              }}
              // Epic sign-in disclosure, drawer-owned — placed at the very
              // bottom, right above Submit (operator request 2026-08-03).
              preFooter={signInSection}
            />
            </>
          );
        }}
      />

    </AppShell>
  );
}
